// EntityDataSetsHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import EditIcon from "@mui/icons-material/Edit";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };

const DEFAULT_PDFS = [
  {
    title: "Entity and DataSets User Manual",
    href: "https://mdimages-test.s3.amazonaws.com/712a2f9d-3f38-4770-8a47-b763cfd0561f_Entity_and_DataSets_User_Manual.pdf",
  },
];

// ---- Static Content ----

const FIELD_PROPERTIES = [
  { property: "Field Name", editableLater: "No — permanent", desc: "The backend ID for this column. Cannot be changed once submitted. Plan carefully." },
  { property: "Label Name", editableLater: "Yes", desc: "The front-end display name shown for this column. Can be edited at any time." },
  { property: "Field Type", editableLater: "Yes", desc: "The data type for the column — e.g., String, Int, Phone Number." },
];

const CREATE_ENTITY_STEPS: StepBlock[] = [
  { title: "Open DataSets", bullets: ["Go to DataSets in the sidebar."] },
  { title: "View Existing DataSets", bullets: ["See all existing DataSets."] },
  { title: "Click Create", bullets: ["Click the Create button at the top-left to start a new DataSet."] },
  { title: "Enter Title and Description", bullets: ["Type a clear Title and Description, then click Submit."] },
];

const DEFINE_COLUMNS_STEPS: StepBlock[] = [
  { title: "Open Update Form", bullets: ["On the new DataSet, click Update Form in the Actions button."] },
  { title: "Note Entity ID", bullets: ["An Entity ID column is created automatically — you don't need to create it yourself."] },
  { title: "Add Columns", bullets: ["For each column, configure Field Name, Label Name, and Field Type."] },
  { title: "Save the Form", bullets: ["Click Save to submit the column definitions."] },
];

const ADD_DATA_STEPS: StepBlock[] = [
  { title: "Open Data View", bullets: ["On the DataSet, click Details in the Actions button."] },
  { title: "Add Single Row", bullets: ["Click the Create button, fill in values, and save."] },
  { title: "Bulk Upload", bullets: ["Click Upload File, download the Sample file, fill in your data, and upload."] },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const EntityDataSetsHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");
    
    return {
      overview: "entity dataset tabular data table spreadsheet columns rows bots forms reference data",
      createDataSet: flatten(CREATE_ENTITY_STEPS) + " title description",
      defineColumns: flatten(DEFINE_COLUMNS_STEPS) + " field name label name field type permanent",
      addData: flatten(ADD_DATA_STEPS) + " single row bulk upload sample file",
      fieldProperties: "field name permanent cannot change label name editable field type",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="Entity & DataSets Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 560,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TableChartIcon />
              <Typography variant="h6" fontWeight={800}>
                Entity & DataSets • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search entity, dataset, columns, bulk upload, field names…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{ mb: 1 }} />
              <Chip label="Create" size="small" variant="outlined" onClick={() => jumpTo("#sec-create")} sx={{ mb: 1 }} />
              <Chip label="Define Columns" size="small" variant="outlined" onClick={() => jumpTo("#sec-columns")} sx={{ mb: 1 }} />
              <Chip label="Add Data" size="small" variant="outlined" onClick={() => jumpTo("#sec-add-data")} sx={{ mb: 1 }} />
              <Chip label="Field Properties" size="small" variant="outlined" onClick={() => jumpTo("#sec-field-props")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="Reusable, tabular data tables for reference data." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Entities and DataSets are reusable, tabular data tables — similar in structure to a spreadsheet, with defined columns and rows.
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    The terms Entity and DataSet are used interchangeably on the platform — both refer to the same underlying tabular data table.
                  </Alert>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography fontWeight={600}>Used By</Typography>
                        <Typography variant="body2">Bots (Conditional Nodes, dynamic List Nodes) and Forms</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography fontWeight={600}>Purpose</Typography>
                        <Typography variant="body2">Validate, match, or display data</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Card>
              </>
            )}

            {/* Create DataSet */}
            {matches("createDataSet") && (
              <>
                <SectionHeader id="sec-create" title="Creating an Entity / DataSet" />
                <StepAccordion steps={CREATE_ENTITY_STEPS} label="Create DataSet" defaultExpanded />
              </>
            )}

            {/* Define Columns */}
            {matches("defineColumns") && (
              <>
                <SectionHeader id="sec-columns" title="Define the Columns" />
                <StepAccordion steps={DEFINE_COLUMNS_STEPS} label="Build the Form" defaultExpanded />
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <strong>Field Name is Permanent:</strong> Once a column's Field Name is submitted, it cannot be changed. Double-check before saving.
                </Alert>
              </>
            )}

            {/* Add Data */}
            {matches("addData") && (
              <>
                <SectionHeader id="sec-add-data" title="Adding Data" />
                <StepAccordion steps={ADD_DATA_STEPS} label="Add Rows" defaultExpanded />
              </>
            )}

            {/* Field Properties */}
            {matches("fieldProperties") && (
              <>
                <SectionHeader id="sec-field-props" title="Field Properties" />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Property</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Editable Later?</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {FIELD_PROPERTIES.map((row) => (
                        <TableRow key={row.property}>
                          <TableCell>{row.property}</TableCell>
                          <TableCell>{row.editableLater}</TableCell>
                          <TableCell>{row.desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="warning">
                  An Entity ID column is created automatically for every row — you do not need to create this yourself.
                </Alert>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default EntityDataSetsHelp;